<script lang="ts" setup>
import { useClaims } from '@auth/useClaims';
import { createErrorMessage } from '@errors/helpers';
import { usePolisGetPolissen } from '@services/polis-public/orval-api/polis-api/polis-api';
import { VButton } from '@shared/component/VButton';
import VContentBox from '@shared/component/VContentBox/VContentBox.vue';
import VHorizontalNavigation from '@shared/component/VHorizontalNavigation';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VPageContainer from '@shared/layout/VPageContainer/VPageContainer.vue';
import { useQuery } from '@tanstack/vue-query';
import { computed, toRefs } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import routes from '../../routes/polisdetails.routes';
import { polisQueries } from './polisQueries';
import SubpolisSelector from './SubpolisSelector.vue';

const props = defineProps<{
  relatienummer: number;
  polisnummer: string;
}>();

const { relatienummer, polisnummer } = toRefs(props);

const router = useRouter();
const route = useRoute();
const claims = useClaims();

const { data, isPending, isError, error } = useQuery({
  ...polisQueries.getDetails(polisnummer),
  staleTime: 60000,
  retry: 3,
});

function isAllowed(path: string, isInternal: boolean, soort: string) {
  if (path === 'roy-data') {
    return isInternal && (soort === 'MR' || soort === 'VR');
  }

  return true;
}

const items = computed(() =>
  routes
    .filter((r) =>
      isAllowed(
        r.path,
        claims.value?.isInternal ?? false,
        data.value?.data?.polis?.soortverzekering,
      ),
    )
    .map((r) => ({
      label: String(r.meta?.title ?? ''),
      key: r.path,
      additionalFields: r.path,
      children: [],
    })),
);

const activeNavItem = computed(() => {
  const isActive = items.value.find((item) => {
    if (route.fullPath.includes(item.additionalFields)) return item;
  });
  return isActive?.key;
});

const { isPending: polissenPending, data: polissen } = usePolisGetPolissen(
  computed(() => ({
    relatienummer: relatienummer.value,
    neemRoyementenMee: true,
    neemBeursPakkettenMee: true,
  })),
  {
    query: {
      select: (result) => result.result,
      retry: 3,
      staleTime: 5 * 60 * 1000,
    },
  },
);

const pakketpolis = computed(() => {
  if (polissenPending.value || !polissen.value) return undefined;

  return polissen.value.find((polis) =>
    polis.subpolissen.some((sub) => sub.polisnummer === polisnummer.value),
  );
});

const terugNaarOverzichtHash = computed(() => {
  const previousRoute = router.options.history.state.back;

  if (typeof previousRoute === 'string' && previousRoute.includes('#open'))
    return previousRoute.substring(previousRoute.indexOf('#'));

  if (!pakketpolis.value) return undefined;
  return `#open=${pakketpolis.value.polisnummer}`;
});
</script>
<template>
  <VPageContainer container-size="sm">
    <template #title>
      <SubpolisSelector :relatienummer="relatienummer" :polisnummer="polisnummer" />
    </template>
    <VContentBox>
      <RouterLink :to="{ name: 'polissen', hash: terugNaarOverzichtHash }">
        <VButton inverted prepend-icon="far fa-chevron-left">Terug naar polissen</VButton>
      </RouterLink>
      <VInfoBox badge-text="error" class="mt-4" type="danger" v-if="isError">
        <div v-html="createErrorMessage(error)"></div>
      </VInfoBox>
      <div v-else>
        <div v-if="isPending" class="mt-4">
          <VSkeleton height="lg" />
          <hr />
        </div>
        <VHorizontalNavigation v-else compact :active-nav-item="activeNavItem" :items="items" />
        <RouterView :key="route.path" v-slot="{ Component }">
          <VSkeleton v-if="isPending" :count="12" height="lg" />
          <component v-else :is="Component" />
        </RouterView>
      </div>
    </VContentBox>
  </VPageContainer>
</template>
